import { isMatch } from 'lodash-es';
import type { Observable } from 'rxjs';
import { map, Subject, tap } from 'rxjs';
import { schema } from '../../../../schema';
import { RowManager } from '../../../../utils/row-manager';

interface AlarmRow extends Record<string, unknown> {
    key: string;
}

interface FormControl {
    getValues: (field: string) => Recipient[] | null | undefined;
    setValue: (field: string, value: Recipient[]) => void;
}

interface Recipient {
    enabledAlarms: string[];
    email: string;
}

interface AlarmRowManagerConfig {
    maxRows: number;
    emptyRow: Omit<AlarmRow, 'key'>;
    keyTemplate: string;
    formControl: FormControl;
}

export class AlarmRowManager extends RowManager<AlarmRow> {
    declare keyTemplate: string;

    declare formControl: FormControl;

    declare usedIndices: number[];

    declare removedKeys$: Subject<string>;

    declare lastIndex: number;

    constructor(config: AlarmRowManagerConfig) {
        super(config);

        // Clean up the enabled alarms when a row gets removed.
        this.removedKeys$.subscribe((removedKeys) => {
            const recipientsKey = 'notificationConfig.recipients';
            const recipients = this.formControl.getValues(recipientsKey) ?? [];
            const filteredRecipients = recipients.map((recipient) => ({
                ...recipient,
                enabledAlarms: recipient.enabledAlarms.filter((key) => !removedKeys.includes(key)),
            }));
            this.formControl.setValue(recipientsKey, filteredRecipients);
        });
    }

    storeConfig(config: AlarmRowManagerConfig): void {
        super.storeConfig(config);

        this.keyTemplate = config.keyTemplate;
        this.formControl = config.formControl;

        this.usedIndices = [];
        this.removedKeys$ = new Subject<string>();
    }

    findBestLastIndex(numbers: number[]): void {
        /*
         * We have to find an index to continue generating alarm indices
         * at that is least likely having the code reuse indices.
         * If the alarm keys are 1, 3, 5, then this would return 6.
         * If the they are 97, 99, 3, then it would return 4.
         */
        const sorted = numbers.sort((a, b) => a - b);
        let maxGap = -1;

        for (let i = 0; i < sorted.length; i++) {
            const current = sorted[i];
            const next = sorted[(i + 1) % sorted.length];

            // JavaScript's module can return negative values,
            // so `+ maxKeys` to make sure it is always positive.
            const gap = (next - current + schema.maxAlarmKeys) % schema.maxAlarmKeys;

            if (gap > maxGap) {
                maxGap = gap;
                this.lastIndex = current;
            }
        }
    }

    // Method to handle incoming rows.
    handleInputRows(input$: Observable<AlarmRow[]>) {
        // Each key is something like `max_alarm_1`, we want to get the `1` for each.
        const regex = new RegExp(this.keyTemplate.replace('{index}', '(\\d+)'));

        return super.handleInputRows(
            input$.pipe(
                // Find the best index to continue at from the `max_alarm_1` keys.
                tap((input) => {
                    // Converts the `max_alarm_1` into a number 1.
                    const numbers = (input ?? []).flatMap((row) => {
                        const match = row.key.match(regex);
                        return match ? [parseInt(match[1], 10)] : [];
                    });

                    // Only call findBestLastIndex once on the first time numbers contains data.
                    if (!this.lastIndex && numbers.length) {
                        this.findBestLastIndex(numbers);

                        // Keep numbers around to keep track of what has already been used.
                        this.usedIndices = numbers;
                    }
                }),
                // Extract the alarm rows from the notification config.
                // E.g. pulls the `maxAlarm` array from the notification config object.
                map((input) => input ?? [])
            )
        );
    }

    isEmptyRow(row: AlarmRow): boolean {
        return isMatch(row, this.emptyRow);
    }

    interpolate(template: string, values: Record<string, number>): string {
        return template.replace(/{(\w+)}/g, (match, key) =>
            key in values ? String(values[key as keyof typeof values]) : match
        );
    }

    createKey(index: number): string {
        return this.interpolate(this.keyTemplate, { index });
    }

    removeRow(index: number): void {
        // Get the get belonging to this row.
        const { key } = this.rows$.getValue()[index];

        // Make sure to keep track of the removed keys. This is needed to cleanup the enabled
        // alarms in the recipients list.
        this.removedKeys$.next(key);

        // Now remove the actual row.
        super.removeRow(index);
    }

    createRow(_rows?: AlarmRow[]): AlarmRow {
        // In case createRow gets called before handleInputRows.
        const lastIndex = this.lastIndex || 0;

        let index = lastIndex;

        // Generate a key and check if it already exists in the rows.
        do {
            // Move the key index and make sure it wraps around.
            index = (index + 1) % schema.maxAlarmKeys;

            // In case we make it back to the lastIndex, we were unable to find an empty key and
            // this would block forever.
            if (index === lastIndex) throw new Error('Out of keys.');

            // Keep searching as long as the index was already used before.
        } while (this.usedIndices.includes(index));

        // Mark this index used.
        this.usedIndices.push(index);

        // Create a key from the discovered index.
        const key = this.createKey(index);

        return { ...this.emptyRow, key };
    }
}
